import { getCurrentQueryParams } from 'utils';

import type { ReservationHoldMessage } from '../api/reservationHold';
import { isAwaitingVerification } from '../api/reservationHold';
import { RESERVATION_ROOT } from '../routes';
import { ReservationCardStatus } from '../types';

/**
 * Remembers that the current deposit was handed over to the reservation flow.
 *
 * The hold signal is one-shot: the hub delivers it once, the session-state response carries it once.
 * The ordinary deposit pipeline, however, keeps going after the hand-over — the submit response,
 * a 3DS child frame or `Await` may still navigate to `/deposit/deposit-processing` afterwards. Without
 * a memory of the hold that later navigation wins and the player is pulled out of the flow.
 * `ReservationHoldGate` reads this flag to send them back.
 *
 * Module memory rather than storage on purpose: leaving the flow exits the widget, so the flag dies
 * with it, and a reload re-learns the hold from the hub (sent on connect) or session-state.
 */
let isHoldMarked = false;

export const markReservationHold = (): void => {
  isHoldMarked = true;
};

export const clearReservationHold = (): void => {
  isHoldMarked = false;
};

export const isReservationHoldMarked = (): boolean => isHoldMarked;

export const getReservationEntryPath = (): string => `${RESERVATION_ROOT}${getCurrentQueryParams() || ''}`;

/**
 * Single rule for "does this payload send the player to the reservation flow", shared by the hub
 * listener and the session-state response.
 *
 * A verified card still produces a brief `awaiting_verification` while the hold is captured.
 * Nothing is asked of the player there, so the ordinary processing page must stay — showing the hold
 * screen would demand a document that is already on file.
 */
export const shouldHandOverToReservation = (
  payload: ReservationHoldMessage | null | undefined,
  cardStatus: ReservationCardStatus | null,
): boolean => isAwaitingVerification(payload) && cardStatus !== ReservationCardStatus.verified;
